import { getSessionStorage, setSessionStorage } from "features/sessionStorage";
import { buildWidgetInstanceId } from "features/widgets";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCollectedData } from "shared/contexts/PageData";
import { useToggle } from "shared/hooks";

import { WidgetContent } from "./Content064.context";

const STORAGE_KEY = "__comparison__selected__courses";

export const WidgetProvider = ({ children, payload }) => {
  const { locale } = useRouter();
  const instanceId = buildWidgetInstanceId("064", payload.id);
  const { courses } = useCollectedData(instanceId, [
    { name: "courses", defaultValue: [] },
  ]);

  const config = useMemo(() => {
    return payload?.config ?? [];
  }, [payload]);

  const [selectedCourses, setSelectCourses] = useState([]);

  const availableCourses = useMemo(() => {
    return courses.filter(({ slug }) => !selectedCourses.includes(slug));
  }, [courses, selectedCourses]);

  const unavailableCourses = useMemo(() => {
    return courses.filter(({ slug }) => selectedCourses.includes(slug));
  }, [courses, selectedCourses]);

  const { opened, onOpen, onClose } = useToggle();

  const onSelectCourse = useCallback(
    (slug) => () => {
      setSelectCourses((current) => [...current, slug]);
      onClose();

      setSessionStorage(STORAGE_KEY, [...selectedCourses, slug]);
    },
    [onClose, selectedCourses]
  );

  const onDeleteCourse = useCallback(
    (slug) => () => {
      setSelectCourses((current) => current.filter((item) => item !== slug));

      setSessionStorage(
        STORAGE_KEY,
        selectedCourses.filter((item) => item !== slug)
      );
    },
    [selectedCourses]
  );

  useEffect(() => {
    const storedData = getSessionStorage(STORAGE_KEY);

    if (storedData !== null) setSelectCourses(JSON.parse(storedData));
  }, []);

  const isAddingDisabled = useMemo(
    () => availableCourses.length === 0,
    [availableCourses.length]
  );

  const availableMetrics = useMemo(() => {
    return config?.filter(({ enabled }) => Boolean(enabled));
  }, [config]);

  const value = {
    locale,
    courses,
    availableCourses,
    unavailableCourses,
    selectedCourses,
    opened,
    onOpen,
    onClose,
    onSelectCourse,
    onDeleteCourse,
    isAddingDisabled,
    availableMetrics,
  };

  return (
    <WidgetContent.Provider value={value}>{children}</WidgetContent.Provider>
  );
};
